import React, { useEffect, useState } from 'react';

import TickIcon from 'icons/ic-tick.svg';
import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';

import { useAccountUserAllowanceQuery } from 'components/hooks/accountUserAllowanceQueries';
import useDebounce from 'components/hooks/useDebounce';
import { useUsersQuery } from 'components/hooks/usersQueries';
import useSidePanel from 'components/hooks/useSidePanel';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import Paginator from 'components/application/Paginator';
import Toast from 'components/application/Toast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import AllowanceBar from 'components/settings/users/AllowanceBar';
import useUserFilters, {
  buildUserFilters,
  checkedAllowanceTypes,
} from 'components/settings/users/hooks/useUserFilters';
import UserManagementTable from 'components/settings/users/UserManagementTable';
import UsersFilterSidePanel from 'components/settings/users/UsersFilterSidePanel';
import UserSidePanel from 'components/settings/users/UserSidePanel';

import { personDisplayName } from 'components/helpers/users';

function buildUserFilterParams(filters) {
  const allowanceTypes = checkedAllowanceTypes(
    filters.allowanceTypeFilter || {},
  );
  const divisionIds = filters.divisionFilter?.selectedIds || [];

  return {
    ...(divisionIds.length > 0 && {
      division_id: { in: divisionIds },
    }),
    ...(allowanceTypes.length > 0 && {
      allowance_type: { in: allowanceTypes.map(_.snakeCase) },
    }),
  };
}

export default function UsersPage({
  areProjectsEnabled,
  divisions,
  initialAction,
  initialUser,
  isMultiDivisionAccount,
  isPersonnelReporteeManagerEnabled,
  isSamlSsoAllowed,
  isSamlSsoEnforced,
  isTeamsAllowed,
  isTrainingRegisterAllowed,
  samlProviderConfiguration,
  toastMessages,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [currentSearch, setCurrentSearch] = useState(
    initialUser?.attributes.email || '',
  );

  const [currentPage, setCurrentPage] = useState(1);

  const {
    handleUserFilterAdd,
    handleUserFilterRemove,
    handleUserFilterValueAdd,
    handleUserFilterValueChange,
    handleUserFilterValueRemove,
    handleUserFiltersCleanup,
    userFilters,
  } = useUserFilters();

  const [isFilterSidePanelOpen, setIsFilterSidePanelOpen] = useState(false);

  const [debouncedCurrentSearch] = useDebounce(currentSearch, 250, {
    onChange: () => setCurrentPage(1),
  });

  const [
    sidePanelIsOpen,
    ,
    openSidePanel,
    closeSidePanel,
    ,
    sidePanelContext,
    setSidePanelContext,
  ] = useSidePanel(!!initialAction, initialAction || 'new');

  const [selectedUserId, setSelectedUserId] = useState(initialUser?.id || null);

  const handleSearchChange = (e) => setCurrentSearch(e.target.value);
  const handleSearchReset = () => setCurrentSearch('');

  const handleFilterSidePanelOpen = () => setIsFilterSidePanelOpen(true);

  const handleFilterSidePanelClose = () => {
    setIsFilterSidePanelOpen(false);
    handleUserFiltersCleanup();
  };

  const handleFilterAdd = (filterKey) => {
    handleUserFilterAdd(filterKey);
    setIsFilterSidePanelOpen(true);
  };

  const handleFilterRemove = (filterKey) => {
    handleUserFilterRemove(filterKey);
    setCurrentPage(1);
  };

  const handleFilterValueAdd = (params) => {
    handleUserFilterValueAdd(params);
    setCurrentPage(1);
  };

  const handleFilterValueChange = (params) => {
    handleUserFilterValueChange(params);
    setCurrentPage(1);
  };

  const handleFilterValueRemove = (params) => {
    handleUserFilterValueRemove(params);
    setCurrentPage(1);
  };

  const isDivisionFilterable = divisions.length > 1;

  const selectedUserFilters = Object.keys(userFilters);

  const userFilterLabels = buildUserFilters({
    isDivisionFilterable,
    translations: {
      allowanceTypeFilter: i18nTShared('allowanceType'),
      divisionFilter: i18nTShared('divisionsParentheses'),
    },
  });

  const usersParams = {
    page: currentPage,
    search: debouncedCurrentSearch,
    filter: {
      ...(divisions.length === 1 && {
        division_id: { in: [divisions[0].id] },
      }),
      ...buildUserFilterParams(userFilters),
    },
  };

  const {
    data: domainUsers,
    isError: isDomainUsersFetchError,
    isFetching: isDomainUsersFetching,
    isLoading: isDomainUsersLoading,
    isSuccess: isDomainUsersSuccess,
  } = useUsersQuery({ params: usersParams });

  const domainUsersInclusions = domainUsers?.included;

  const isDomainUsersDataPending =
    isDomainUsersLoading || isDomainUsersFetching || isDomainUsersFetchError;

  const { data: domainAccountUserAllowance } = useAccountUserAllowanceQuery();

  const handleAddUserClick = () => {
    handleFilterSidePanelClose();
    setSidePanelContext('new');

    if (!isManagingMultiDivisions) {
      setSelectedUserId(null);
      openSidePanel();
    } else {
      window.location.assign('/users/new');
    }
  };

  const handleClose = () => {
    setSelectedUserId(null);
    closeSidePanel();
  };

  const handleEditUserClick = (userId) => {
    handleFilterSidePanelClose();

    if (!isManagingMultiDivisions) {
      setSidePanelContext('edit');
      setSelectedUserId(userId);
      openSidePanel();
    } else {
      window.location.assign(`/users/${userId}/edit`);
    }
  };

  const handleUserCreated = (serializedCreatedUser) => {
    handleClose();

    breadBoard.addToast(
      <UserCreatedToast
        handleBurnToast={breadBoard.handleBurnToast}
        isSamlSsoAllowed={isSamlSsoAllowed}
        user={serializedCreatedUser}
      />,
    );
  };

  const handleUserUpdated = (serializedUpdatedUser) => {
    handleClose();

    breadBoard.addToast(
      <UserUpdatedToast
        handleBurnToast={breadBoard.handleBurnToast}
        user={serializedUpdatedUser}
      />,
    );
  };

  const handleUserRestored = (serializedRestoredUser) => {
    handleClose();

    breadBoard.addToast(
      <UserRestoredToast
        handleBurnToast={breadBoard.handleBurnToast}
        user={serializedRestoredUser}
      />,
    );
  };

  useEffect(() => {
    if (toastMessages) {
      // only supporting 1
      const toast = toastMessages[0];
      const ToastComponent = {
        userCreated: UserCreatedToast,
        userUpdated: UserUpdatedToast,
        userRestored: UserRestoredToast,
      }[toast.type];

      breadBoard.addToast(
        <ToastComponent
          handleBurnToast={breadBoard.handleBurnToast}
          isSamlSsoAllowed={isSamlSsoAllowed}
          {...toast.data}
        />,
      );
    }
  }, []);

  const selectedUser =
    selectedUserId &&
    domainUsers?.data.find((user) => user.id === selectedUserId);

  const selectedDomainUser = selectedUser && {
    data: selectedUser,
    included: Object.values(domainUsersInclusions.accessControl).filter(
      (accessControl) =>
        accessControl.relationships.user.data.id === selectedUser.id,
    ),
  };

  const isSuperUser = ['primary', 'administrator'].includes(
    currentActor.user.attributes.accessType,
  );
  const isManagingMultiDivisions = isMultiDivisionAccount && isSuperUser;

  const {
    atFullUserCapacity,
    atPersonnelReporteeManagerCapacity,
    fullUserAllowanceConsumptionCount,
    fullUserAllowanceCount,
    personnelReporteeManagerAllowanceConsumptionCount,
    personnelReporteeManagerAllowanceCount,
  } = domainAccountUserAllowance?.data.attributes || {};

  return (
    isDomainUsersSuccess && (
      <>
        <AllowanceBar
          areProjectsEnabled={areProjectsEnabled}
          fullUserAllowanceConsumptionCount={fullUserAllowanceConsumptionCount}
          fullUserAllowanceCount={fullUserAllowanceCount}
          isAtFullUserCapacity={atFullUserCapacity}
          isAtPersonnelReporteeManagerCapacity={
            atPersonnelReporteeManagerCapacity
          }
          isPersonnelReporteeManagerEnabled={isPersonnelReporteeManagerEnabled}
          personnelReporteeManagerAllowanceConsumptionCount={
            personnelReporteeManagerAllowanceConsumptionCount
          }
          personnelReporteeManagerAllowanceCount={
            personnelReporteeManagerAllowanceCount
          }
        />
        <UserManagementTable
          areProjectsEnabled={areProjectsEnabled}
          domainUsers={domainUsers}
          filterOptions={{
            filterLabels: userFilterLabels,
            onAdd: handleFilterAdd,
            onOpen: handleFilterSidePanelOpen,
            selectedFilters: selectedUserFilters,
          }}
          isAtFullUserCapacity={atFullUserCapacity}
          isDataPending={isDomainUsersDataPending}
          isManagingMultiDivisions={isManagingMultiDivisions}
          isSamlSsoEnforced={isSamlSsoEnforced}
          isTrainingRegisterAllowed={isTrainingRegisterAllowed}
          onAddUserClick={handleAddUserClick}
          onEditClick={handleEditUserClick}
          searchOptions={{
            value: currentSearch,
            debouncedValue: debouncedCurrentSearch,
            onChange: handleSearchChange,
            onReset: handleSearchReset,
          }}
        />
        {domainUsers.meta.totalPages > 1 && (
          <div className='tw-mt-20 tw-text-center'>
            <Paginator
              currentPage={domainUsers.meta.currentPage}
              onClick={(event) =>
                setCurrentPage(
                  Number(event.currentTarget.getAttribute('data-page')),
                )
              }
              totalPages={domainUsers.meta.totalPages}
            />
          </div>
        )}
        <UsersFilterSidePanel
          filterLabels={userFilterLabels}
          filters={userFilters}
          isSidePanelOpen={isFilterSidePanelOpen}
          onClose={handleFilterSidePanelClose}
          onFilterAdd={handleUserFilterAdd}
          onFilterRemove={handleFilterRemove}
          onFilterValueAdd={handleFilterValueAdd}
          onFilterValueChange={handleFilterValueChange}
          onFilterValueRemove={handleFilterValueRemove}
          selectedFilters={selectedUserFilters}
        />
        {!isManagingMultiDivisions && (
          <UserSidePanel
            areProjectsEnabled={areProjectsEnabled}
            context={sidePanelContext}
            division={divisions[0]}
            domainUser={selectedDomainUser}
            isAtFullUserCapacity={atFullUserCapacity}
            isAtPersonnelReporteeManagerCapacity={
              atPersonnelReporteeManagerCapacity
            }
            isMultiDivisionAccount={isMultiDivisionAccount}
            isOpen={sidePanelIsOpen}
            isSamlSsoAllowed={isSamlSsoAllowed}
            isTeamsAllowed={isTeamsAllowed}
            key={selectedUserId}
            onClose={handleClose}
            onCreateSuccess={handleUserCreated}
            onRestoreSuccess={handleUserRestored}
            onUpdateSuccess={handleUserUpdated}
            samlProviderConfiguration={samlProviderConfiguration}
          />
        )}
      </>
    )
  );
}

export function UserCreatedToast({ handleBurnToast, isSamlSsoAllowed, user }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'pages.settings.UsersPage',
  });

  const { companyName, formattedName, name, personnel, personnelProfilePath } =
    getPersonnelToastDetails({ user });

  return (
    <Toast
      burn={handleBurnToast}
      burnTimer={10000}
      contents={
        <>
          <div className='toast__icon tw-bg-green-500'>
            <TickIcon
              className='[&_polygon]:tw-fill-white'
              height={20}
              width={20}
            />
          </div>
          <div className='toast__text'>
            {personnel ?
              <Trans
                components={{
                  button: (
                    <OutlinedButton
                      className='tw-mt-3'
                      color='grey'
                      href={personnelProfilePath}
                      size='sm'
                    />
                  ),
                  div: <div />,
                }}
                i18nKey={`pages.settings.UsersPage.handleUserCreated.success.${isSamlSsoAllowed ? 'added' : 'invited'}WithPersonnel`}
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  companyName:
                    companyName.length > 20 ?
                      `${companyName.slice(0, 20)}..`
                    : companyName,
                  name: formattedName,
                }}
              />
            : i18nT(
                `handleUserCreated.success.${isSamlSsoAllowed ? 'added' : 'invited'}`,
                { interpolation: { escapeValue: false }, name: formattedName },
              )
            }
          </div>
        </>
      }
      modifiers='no-undo'
      toastKey={`userCreatedToast--${name}`}
    />
  );
}

function UserUpdatedToast({ handleBurnToast, user }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'pages.settings.UsersPage',
  });

  const name = personDisplayName({ attributes: user.data.attributes });

  return (
    <Toast
      burn={handleBurnToast}
      burnTimer={5000}
      contents={
        <>
          <div className='toast__icon tw-bg-green-500'>
            <TickIcon
              className='[&_polygon]:tw-fill-white'
              height={20}
              width={20}
            />
          </div>
          <div className='toast__text'>
            {i18nT('handleUserUpdated.success', {
              interpolation: { escapeValue: false },
              name,
            })}
          </div>
        </>
      }
      modifiers='no-undo'
      toastKey={`userUpdatedToast--${name}`}
    />
  );
}

export function UserRestoredToast({ handleBurnToast, user }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'pages.settings.UsersPage',
  });

  const { companyName, formattedName, name, personnel, personnelProfilePath } =
    getPersonnelToastDetails({ user });

  return (
    <Toast
      burn={handleBurnToast}
      burnTimer={10000}
      contents={
        <>
          <div className='toast__icon tw-bg-green-500'>
            <TickIcon
              className='[&_polygon]:tw-fill-white'
              height={20}
              width={20}
            />
          </div>
          <div className='toast__text'>
            {personnel ?
              <Trans
                components={{
                  button: (
                    <OutlinedButton
                      className='tw-mt-3'
                      color='grey'
                      href={personnelProfilePath}
                      size='sm'
                    />
                  ),
                  div: <div />,
                }}
                i18nKey='pages.settings.UsersPage.handleUserRestored.success.restoredWithPersonnel'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  companyName:
                    companyName.length > 20 ?
                      `${companyName.slice(0, 20)}..`
                    : companyName,
                  name: formattedName,
                }}
              />
            : i18nT('handleUserRestored.success.restored', {
                interpolation: { escapeValue: false },
                name: formattedName,
              })
            }
          </div>
        </>
      }
      modifiers='no-undo'
      toastKey={`userRestoredToast--${name}`}
    />
  );
}

function getPersonnelToastDetails({ user }) {
  const name = user.data.attributes.firstName;
  const formattedName = name.length > 10 ? `${name.slice(0, 10)}...` : name;

  const personnel =
    user.meta.personnelGenerated &&
    user.included.find((inclusion) => inclusion.type === 'personnel');

  const personnelProfilePath = personnel?.links?.profile;

  const companyName =
    personnel &&
    user.included.find(
      (inclusion) =>
        inclusion.type === 'company' &&
        inclusion.id === personnel.relationships.company.data.id,
    ).attributes.name;

  return {
    name,
    formattedName,
    personnel,
    personnelProfilePath,
    companyName,
  };
}
